import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Settings, Trash2, AlertTriangle, Users, Shield, Target, TrendingUp, Zap, BarChart3, UserX } from 'lucide-react';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { deleteBoard, updateBoard, removeBoardMember } from '../firebase/firestore';
import { Board, PriorityScoringConfig } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { DEFAULT_PRIORITY_SCORING_CONFIG } from '../utils/priorityScore';

interface BoardSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  board: Board;
  onBoardDeleted: () => void;
}

const BoardSettingsModal: React.FC<BoardSettingsModalProps> = ({
  isOpen,
  onClose,
  board,
  onBoardDeleted,
}) => {
  const { user } = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [boardName, setBoardName] = useState(board.name);
  const [isSavingName, setIsSavingName] = useState(false);
  const [scoringConfig, setScoringConfig] = useState<PriorityScoringConfig>(
    board.priorityScoringConfig || DEFAULT_PRIORITY_SCORING_CONFIG
  );
  const [isSavingScoring, setIsSavingScoring] = useState(false);
  const [removingMember, setRemovingMember] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && board) {
      setBoardName(board.name);
    }
  }, [isOpen, board, board.name]);

  const handleSaveBoardName = async () => {
    const trimmed = boardName.trim();
    if (!trimmed || trimmed === board.name) return;
    setIsSavingName(true);
    try {
      await updateBoard(board.id, { name: trimmed });
      alert('Board name updated.');
    } catch (error) {
      console.error('Error updating board name:', error);
      alert('Failed to update board name. Please try again.');
    } finally {
      setIsSavingName(false);
    }
  };

  const handleDeleteBoard = async () => {
    setIsDeleting(true);
    try {
      await deleteBoard(board.id);
      onBoardDeleted();
      onClose();
    } catch (error) {
      console.error('Error deleting board:', error);
      alert('Failed to delete board. Please try again.');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleSaveScoringConfig = async () => {
    setIsSavingScoring(true);
    try {
      await updateBoard(board.id, { priorityScoringConfig: scoringConfig });
      alert('Priority scoring configuration saved successfully!');
    } catch (error) {
      console.error('Error saving scoring config:', error);
      alert('Failed to save scoring configuration. Please try again.');
    } finally {
      setIsSavingScoring(false);
    }
  };

  const handleResetScoringConfig = () => {
    if (window.confirm('Are you sure you want to reset to default scoring configuration?')) {
      setScoringConfig(DEFAULT_PRIORITY_SCORING_CONFIG);
    }
  };

  const handleRemoveMember = async (memberId: string, memberEmail: string) => {
    if (!window.confirm(`Are you sure you want to remove ${memberEmail} from this board?`)) {
      return;
    }

    setRemovingMember(memberId);
    try {
      await removeBoardMember(board.id, memberId);
      alert(`${memberEmail} has been removed from the board.`);
    } catch (error) {
      console.error('Error removing member:', error);
      alert('Failed to remove member. Please try again.');
    } finally {
      setRemovingMember(null);
    }
  };

  const getRoleDescription = (role: string) => {
    switch (role) {
      case 'owner':
        return 'Full control over the board, including deletion and member management';
      case 'admin':
        return 'Can manage members, edit board settings, and delete products';
      case 'editor':
        return 'Can add, edit, and delete products, invite users';
      case 'viewer':
        return 'Can view products and add comments';
      default:
        return '';
    }
  };

  const currentUserRole = board.members.find(member => 
    member.email === user?.email
  )?.role || 'viewer';

  const canDeleteBoard = currentUserRole === 'owner';

  // Add escape key functionality
  useEscapeKey(onClose, isOpen);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-white/15 backdrop-blur-xl z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="glass-effect rounded-3xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-r from-satsuma-500 to-heian-500 rounded-xl">
                  <Settings size={24} className="text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Board Settings</h2>
                  <p className="text-gray-600 text-sm">{currentUserRole === 'owner' ? boardName : board.name}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Board Information */}
            <div className="space-y-6">
              {/* Board Details */}
              <div className="glass-card p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <Shield size={20} className="mr-2 text-satsuma-600" />
                  Board Information
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <span className="text-gray-600">Name:</span>
                    {currentUserRole === 'owner' ? (
                      <div className="flex items-center gap-2 flex-1 sm:flex-initial sm:max-w-xs">
                        <input
                          type="text"
                          value={boardName}
                          onChange={(e) => setBoardName(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleSaveBoardName()}
                          className="flex-1 min-w-0 px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-satsuma-500"
                          placeholder="Board name"
                        />
                        <button
                          type="button"
                          onClick={handleSaveBoardName}
                          disabled={isSavingName || !boardName.trim() || boardName.trim() === board.name}
                          className="shrink-0 px-3 py-1.5 bg-satsuma-600 text-white text-sm font-medium rounded-lg hover:bg-satsuma-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSavingName ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    ) : (
                      <span className="font-medium">{board.name}</span>
                    )}
                  </div>
                  {board.description && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Description:</span>
                      <span className="font-medium">{board.description}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Created:</span>
                    <span className="font-medium">
                      {board.createdAt ? new Date(board.createdAt).toLocaleDateString() : 'Unknown date'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Members:</span>
                    <span className="font-medium">{board.members.length}</span>
                  </div>
                </div>
              </div>

              {/* Your Role */}
              <div className="glass-card p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <Users size={20} className="mr-2 text-heian-600" />
                  Your Role
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      currentUserRole === 'owner' ? 'bg-red-100 text-red-700' :
                      currentUserRole === 'admin' ? 'bg-orange-100 text-orange-700' :
                      currentUserRole === 'editor' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {currentUserRole.charAt(0).toUpperCase() + currentUserRole.slice(1)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {getRoleDescription(currentUserRole)}
                  </p>
                </div>
              </div>

              {/* Priority Scoring Configuration */}
              {canDeleteBoard && (
                <div className="glass-card p-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <BarChart3 size={20} className="mr-2 text-satsuma-600" />
                    Priority Scoring Configuration
                  </h3>
                            <p className="text-sm text-gray-600 mb-4">
            Adjust the percentage weight of different factors in priority score calculations for this board. Total must equal 100%.
          </p>
                  
                  <div className="space-y-4">
                    {/* Market Potential */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Target size={16} className="text-satsuma-600" />
                        <span className="text-sm font-medium">Market Potential</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={scoringConfig.marketPotential}
                        onChange={(e) => setScoringConfig(prev => ({ ...prev, marketPotential: parseInt(e.target.value) }))}
                        className="w-24"
                      />
                      <span className="text-sm text-gray-600 w-12 text-right">{scoringConfig.marketPotential}%</span>
                    </div>

                    {/* GTM Ready Percent */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <TrendingUp size={16} className="text-heian-600" />
                        <span className="text-sm font-medium">GTM Ready %</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={scoringConfig.gtmReadyPercent}
                        onChange={(e) => setScoringConfig(prev => ({ ...prev, gtmReadyPercent: parseInt(e.target.value) }))}
                        className="w-24"
                      />
                      <span className="text-sm text-gray-600 w-12 text-right">{scoringConfig.gtmReadyPercent}%</span>
                    </div>

                    {/* Status */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Zap size={16} className="text-matcha-600" />
                        <span className="text-sm font-medium">Status</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={scoringConfig.status}
                        onChange={(e) => setScoringConfig(prev => ({ ...prev, status: parseInt(e.target.value) }))}
                        className="w-24"
                      />
                      <span className="text-sm text-gray-600 w-12 text-right">{scoringConfig.status}%</span>
                    </div>

                    {/* Effort Level */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Zap size={16} className="text-sakura-600" />
                        <span className="text-sm font-medium">Effort Level</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={scoringConfig.effortLevel}
                        onChange={(e) => setScoringConfig(prev => ({ ...prev, effortLevel: parseInt(e.target.value) }))}
                        className="w-24"
                      />
                      <span className="text-sm text-gray-600 w-12 text-right">{scoringConfig.effortLevel}%</span>
                    </div>

                    {/* Market Crowding */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Target size={16} className="text-blue-600" />
                        <span className="text-sm font-medium">Market Crowding</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={scoringConfig.marketCrowding}
                        onChange={(e) => setScoringConfig(prev => ({ ...prev, marketCrowding: parseInt(e.target.value) }))}
                        className="w-24"
                      />
                      <span className="text-sm text-gray-600 w-12 text-right">{scoringConfig.marketCrowding}%</span>
                    </div>

                    {/* Market Type */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Target size={16} className="text-purple-600" />
                        <span className="text-sm font-medium">Market Type</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={scoringConfig.marketType}
                        onChange={(e) => setScoringConfig(prev => ({ ...prev, marketType: parseInt(e.target.value) }))}
                        className="w-24"
                      />
                      <span className="text-sm text-gray-600 w-12 text-right">{scoringConfig.marketType}%</span>
                    </div>

                    {/* Business Model */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <BarChart3 size={16} className="text-indigo-600" />
                        <span className="text-sm font-medium">Business Model</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={scoringConfig.businessModel}
                        onChange={(e) => setScoringConfig(prev => ({ ...prev, businessModel: parseInt(e.target.value) }))}
                        className="w-24"
                      />
                      <span className="text-sm text-gray-600 w-12 text-right">{scoringConfig.businessModel}%</span>
                    </div>

                    {/* Support Overhead */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Zap size={16} className="text-orange-600" />
                        <span className="text-sm font-medium">Support Overhead</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={scoringConfig.supportOverhead}
                        onChange={(e) => setScoringConfig(prev => ({ ...prev, supportOverhead: parseInt(e.target.value) }))}
                        className="w-24"
                      />
                      <span className="text-sm text-gray-600 w-12 text-right">{scoringConfig.supportOverhead}%</span>
                    </div>

                    {/* Features */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Target size={16} className="text-green-600" />
                        <span className="text-sm font-medium">Features</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={scoringConfig.features}
                        onChange={(e) => setScoringConfig(prev => ({ ...prev, features: parseInt(e.target.value) }))}
                        className="w-24"
                      />
                      <span className="text-sm text-gray-600 w-12 text-right">{scoringConfig.features}%</span>
                    </div>

                    {/* Competitors */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Users size={16} className="text-red-600" />
                        <span className="text-sm font-medium">Competitors</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={scoringConfig.competitors}
                        onChange={(e) => setScoringConfig(prev => ({ ...prev, competitors: parseInt(e.target.value) }))}
                        className="w-24"
                      />
                      <span className="text-sm text-gray-600 w-12 text-right">{scoringConfig.competitors}%</span>
                    </div>

                    {/* Age */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <TrendingUp size={16} className="text-gray-600" />
                        <span className="text-sm font-medium">Age</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={scoringConfig.age}
                        onChange={(e) => setScoringConfig(prev => ({ ...prev, age: parseInt(e.target.value) }))}
                        className="w-24"
                      />
                      <span className="text-sm text-gray-600 w-12 text-right">{scoringConfig.age}%</span>
                    </div>
                  </div>

                  {/* Total Score Display */}
                  <div className={`mt-4 p-3 border rounded-lg ${
                    Object.values(scoringConfig).reduce((sum, value) => sum + value, 0) === 100 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium ${
                        Object.values(scoringConfig).reduce((sum, value) => sum + value, 0) === 100 
                          ? 'text-green-800' 
                          : 'text-red-800'
                      }`}>
                        Total Weight: {Object.values(scoringConfig).reduce((sum, value) => sum + value, 0)}%
                      </span>
                      <span className={`text-lg font-bold ${
                        Object.values(scoringConfig).reduce((sum, value) => sum + value, 0) === 100 
                          ? 'text-green-800' 
                          : 'text-red-800'
                      }`}>
                        {Object.values(scoringConfig).reduce((sum, value) => sum + value, 0) === 100 ? '✓ Valid' : '✗ Must equal 100%'}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-3 mt-4">
                    <button
                      onClick={handleSaveScoringConfig}
                      disabled={isSavingScoring || Object.values(scoringConfig).reduce((sum, value) => sum + value, 0) !== 100}
                      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {isSavingScoring ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Settings size={16} />
                      )}
                      <span>{isSavingScoring ? 'Saving...' : 'Save Configuration'}</span>
                    </button>
                    <button
                      onClick={handleResetScoringConfig}
                      className="btn-secondary flex items-center space-x-2"
                    >
                      <Zap size={16} />
                      <span>Reset to Default</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Member Management */}
              {canDeleteBoard && (
                <div className="glass-card p-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <Users size={20} className="mr-2 text-satsuma-600" />
                    Member Management
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Manage board members. You can remove members from the board.
                  </p>
                  
                  <div className="space-y-3">
                    {board.members.map((member) => (
                      <div key={member.userId} className="flex items-center justify-between p-3 bg-white/30 rounded-lg border border-white/20">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-gradient-to-r from-satsuma-500 to-heian-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-medium">
                              {member.email.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-800">{member.email}</p>
                            <div className="flex items-center space-x-2">
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                member.role === 'owner' ? 'bg-purple-100 text-purple-700' :
                                member.role === 'admin' ? 'bg-red-100 text-red-700' :
                                member.role === 'editor' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                              </span>
                              <span className="text-xs text-gray-500">
                                Joined {member.joinedAt ? new Date(member.joinedAt).toLocaleDateString() : 'Unknown date'}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {member.role !== 'owner' && (
                          <button
                            onClick={() => handleRemoveMember(member.userId, member.email)}
                            disabled={removingMember === member.userId}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                            title="Remove member"
                          >
                            {removingMember === member.userId ? (
                              <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <UserX size={16} />
                            )}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Users size={16} className="text-blue-600" />
                      <span className="text-sm text-blue-800">
                        {board.members.length} member{board.members.length !== 1 ? 's' : ''} total
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Danger Zone */}
              {canDeleteBoard && (
                <div className="glass-card p-4 border border-red-200">
                  <h3 className="text-lg font-semibold text-red-800 mb-3 flex items-center">
                    <AlertTriangle size={20} className="mr-2 text-red-600" />
                    Danger Zone
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    These actions cannot be undone. Please proceed with caution.
                  </p>
                  
                  {!showDeleteConfirm ? (
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="btn-secondary text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400 flex items-center space-x-2"
                    >
                      <Trash2 size={16} />
                      <span>Delete Board</span>
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-sm text-red-700 font-medium mb-2">
                          ⚠️ Are you sure you want to delete this board?
                        </p>
                        <p className="text-xs text-red-600">
                          This will permanently delete "{board.name}" and all its products, comments, and activities. 
                          This action cannot be undone.
                        </p>
                      </div>
                      <div className="flex space-x-3">
                        <button
                          onClick={handleDeleteBoard}
                          disabled={isDeleting}
                          className="btn-primary bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        >
                          {isDeleting ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Trash2 size={16} />
                          )}
                          <span>{isDeleting ? 'Deleting...' : 'Yes, Delete Board'}</span>
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(false)}
                          disabled={isDeleting}
                          className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!canDeleteBoard && (
                <div className="glass-card p-4 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <AlertTriangle size={20} className="mr-2 text-gray-600" />
                    Board Management
                  </h3>
                  <p className="text-sm text-gray-600">
                    Only board owners can delete boards. Contact the board owner if you need to delete this board.
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BoardSettingsModal;
